import React from 'react';

export interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-neutral-200';

  const variantClasses: Record<string, string> = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-lg',
  };

  const animationClasses: Record<string, string> = {
    pulse: 'animate-pulse',
    wave: 'skeleton-wave',
    none: '',
  };

  const style: React.CSSProperties = {
    width: width ?? (variant === 'circular' ? '40px' : '100%'),
    height: height ?? (variant === 'text' ? '1em' : variant === 'circular' ? '40px' : 'auto'),
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

// Pre-built skeleton patterns
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        width={i === lines - 1 ? '60%' : '100%'}
        height="0.875rem"
      />
    ))}
  </div>
);

export const SkeletonAvatar: React.FC<{ size?: 'sm' | 'md' | 'lg'; className?: string }> = ({
  size = 'md',
  className = ''
}) => {
  const sizes = { sm: 32, md: 40, lg: 48 };
  return <Skeleton variant="circular" width={sizes[size]} height={sizes[size]} className={className} />;
};

export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`p-4 bg-surface rounded-xl border border-neutral-200 ${className}`}>
    <div className="flex items-center gap-3 mb-4">
      <SkeletonAvatar />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="40%" height="1rem" />
        <Skeleton variant="text" width="60%" height="0.75rem" />
      </div>
    </div>
    <SkeletonText lines={2} />
  </div>
);

export const SkeletonTableRow: React.FC<{ columns?: number; className?: string }> = ({
  columns = 4,
  className = ''
}) => (
  <tr className={className}>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton variant="text" width={i === 0 ? '80%' : '60%'} height="0.875rem" />
      </td>
    ))}
  </tr>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number; className?: string }> = ({
  rows = 5,
  columns = 4,
  className = ''
}) => (
  <div className={`overflow-hidden rounded-xl border border-neutral-200 bg-surface ${className}`}>
    <table className="w-full">
      <thead>
        <tr className="bg-neutral-50 border-b border-neutral-200">
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton variant="text" width="70%" height="0.75rem" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

export const SkeletonStatCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`p-4 bg-surface rounded-xl border border-neutral-200 ${className}`}>
    <div className="flex items-center gap-3 mb-3">
      <Skeleton variant="rounded" width={40} height={40} />
      <Skeleton variant="text" width="50%" height="0.875rem" />
    </div>
    <Skeleton variant="text" width="40%" height="1.5rem" />
  </div>
);

export const SkeletonList: React.FC<{ items?: number; className?: string }> = ({
  items = 5,
  className = ''
}) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-neutral-100">
        <SkeletonAvatar size="sm" />
        <div className="flex-1 space-y-1.5">
          <Skeleton variant="text" width="50%" height="0.875rem" />
          <Skeleton variant="text" width="30%" height="0.75rem" />
        </div>
        <Skeleton variant="rounded" width={60} height={24} />
      </div>
    ))}
  </div>
);

export default Skeleton;
